import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Database,
  Download,
  Trash2,
  Loader2,
  Plus,
  ShieldAlert,
  Archive,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function BackupPanel({ currentUser }) {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(null);

  const isSuperAdmin = currentUser?.role === "super_admin";

  const load = async () => {
    try {
      const r = await api.get("/admin/backup/list");
      setBackups(r.data.backups || []);
    } catch (e) {
      if (e.response?.status !== 401) {
        toast.error(e.response?.data?.detail || "Gagal memuat daftar backup");
      }
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    setCreating(true);
    try {
      const r = await api.post("/admin/backup");
      toast.success(`Backup berhasil dibuat: ${r.data.filename}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal membuat backup");
    } finally {
      setCreating(false);
    }
  };

  const downloadBackup = async (filename) => {
    setDownloadingFile(filename);
    try {
      const r = await api.get(`/admin/backup/download/${filename}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("File backup terunduh");
    } catch (e) {
      toast.error("Gagal mengunduh backup");
    } finally {
      setDownloadingFile(null);
    }
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm(`Hapus backup ${filename}? Aksi ini tidak bisa dibatalkan.`)) return;
    try {
      await api.delete(`/admin/backup/${filename}`);
      toast.success("Backup dihapus");
      setBackups((prev) => prev.filter((b) => b.filename !== filename));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus backup");
    }
  };

  useEffect(() => {
    if (isSuperAdmin) load();
    else setLoading(false);
  }, [isSuperAdmin]);

  if (!isSuperAdmin) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
        <ShieldAlert className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <div className="text-sm text-slate-500">
          Hanya <b>Super Admin</b> yang dapat mengakses menu Backup.
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-red-50 text-[#DC2626] flex items-center justify-center flex-shrink-0">
              <Database className="w-5 h-5" strokeWidth={2} />
            </div>
            <div>
              <div className="font-display font-bold text-lg text-slate-900">
                Backup Database
              </div>
              <div className="text-sm text-slate-500 mt-0.5 leading-relaxed">
                Buat cadangan seluruh data (leads, percakapan, admin) dalam bentuk
                arsip terkompresi. File dapat diunduh dan disimpan aman di lokal.
              </div>
            </div>
          </div>
          <button
            data-testid={ADMIN.backupCreateBtn}
            onClick={createBackup}
            disabled={creating}
            className="bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-60 text-white text-sm font-medium py-2.5 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors whitespace-nowrap self-start"
          >
            {creating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Membuat...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" /> Buat Backup Baru
              </>
            )}
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50/60">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">
            Riwayat Backup
          </div>
          <div className="text-xs text-slate-500">
            {backups.length} file
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : backups.length === 0 ? (
          <div
            data-testid={ADMIN.backupEmptyState}
            className="px-6 py-14 text-center"
          >
            <Archive className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <div className="text-sm text-slate-500">
              Belum ada backup. Klik <b>Buat Backup Baru</b> untuk mulai.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-4 py-3 font-medium">Nama File</th>
                  <th className="px-4 py-3 font-medium">Ukuran</th>
                  <th className="px-4 py-3 font-medium">Waktu Dibuat</th>
                  <th className="px-4 py-3 font-medium text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((b) => (
                  <tr
                    key={b.filename}
                    data-testid={ADMIN.backupRow}
                    className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors"
                  >
                    <td className="px-4 py-4">
                      <div className="font-mono text-xs text-slate-800 truncate max-w-[280px]">
                        {b.filename}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-700">
                      {formatBytes(b.size)}
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-700">
                      {formatDate(b.created_at)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          data-testid={ADMIN.backupDownloadBtn}
                          onClick={() => downloadBackup(b.filename)}
                          disabled={downloadingFile === b.filename}
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-[#DC2626] hover:bg-red-50 disabled:opacity-60 px-2.5 py-1.5 rounded-lg transition-colors"
                          title="Download"
                        >
                          {downloadingFile === b.filename ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Download className="w-3.5 h-3.5" />
                          )}
                          Download
                        </button>
                        <button
                          data-testid={ADMIN.backupDeleteBtn}
                          onClick={() => deleteBackup(b.filename)}
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-red-600 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition-colors"
                          title="Hapus"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Hapus
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-900 leading-relaxed">
        <b>Tips:</b> File backup <code className="bg-amber-100 px-1 py-0.5 rounded font-mono">.json.gz</code> berisi
        seluruh koleksi database dalam format JSON (BSON Extended). Simpan di storage aman
        (Google Drive, Dropbox, atau server lain). Untuk membaca isinya cukup ekstrak dengan
        <code className="bg-amber-100 px-1 py-0.5 rounded font-mono">gunzip &lt;file&gt;</code>.
      </div>
    </motion.div>
  );
}
